import React from 'react';
import type { Portfolio, Fund } from 'venn-api';
import type { SearchMenuItem } from '../../search-menu';
import { AllocationSearchProvider, useAllocationSearch } from './AllocationSearchContext';
import { AllocationSearchConstituentView } from './AllocationSearchConstituentView';
import { AllocationSearchInvestmentView } from './AllocationSearchInvestmentView';
import { AllocationSearchStrategyView } from './AllocationSearchStrategyView';
import { useConstituentPortfolios } from './hooks/useConstituentPortfolios';
import { isEmpty } from 'lodash';

interface AllocationSearchContainerProps {
  nodeRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onSingleSelectFund: (item: SearchMenuItem, constituentId?: number) => void;
  onStrategyAssignment?: (fund: Fund, strategyId: string, constituentId?: number) => void;
  strategy: Portfolio;
}

interface AllocationSearchContentProps extends AllocationSearchContainerProps {
  constituents: Portfolio[] | null;
}

const AllocationSearchContent: React.FC<AllocationSearchContentProps> = ({
  nodeRef,
  onClose,
  strategy,
  constituents,
}) => {
  const { view } = useAllocationSearch();

  if (view === 'STRATEGY_ASSIGNMENT') {
    return <AllocationSearchStrategyView nodeRef={nodeRef} onClose={onClose} />;
  }

  if (view === 'CONSTITUENT_SELECTION' && constituents) {
    return <AllocationSearchConstituentView nodeRef={nodeRef} onClose={onClose} constituents={constituents} />;
  }

  return <AllocationSearchInvestmentView nodeRef={nodeRef} onClose={onClose} strategy={strategy} />;
};

export const AllocationSearchContainer: React.FC<AllocationSearchContainerProps> = (props) => {
  const { constituents, isLoading } = useConstituentPortfolios(props.strategy);

  if (isLoading) {
    return null;
  }

  const initialView = isEmpty(constituents) ? 'INVESTMENT_SEARCH' : 'CONSTITUENT_SELECTION';

  return (
    <AllocationSearchProvider
      initialView={initialView}
      onSingleSelectFund={props.onSingleSelectFund}
      onStrategyAssignment={props.onStrategyAssignment}
    >
      <AllocationSearchContent {...props} constituents={constituents} />
    </AllocationSearchProvider>
  );
};
